import {
  CheckboxWithLabel,
  FreeSoloAutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import {
  Button,
  Chip,
  FormHelperText,
  MenuItem,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { FormikProps } from 'formik'
import React from 'react'

import { DatePickerInputWithLabel } from '../../components/DatePickerInputWithLabel'
import SectionTitle from '../../components/SectionTitle'
import { DateObject } from '../../helpers/date'
import {
  SERVICE_NAME_PATTERN,
  joinTraceBoundary,
  splitServices,
  traceDatePart,
  traceTimePart
} from '../../helpers/diagnosticTrace'
import {
  SupportContextCapabilities,
  supportContextReadiness,
  unavailableContextForProduct
} from '../../helpers/supportCapabilities'
import { TICKET_PRODUCT_SCOPES } from '../../helpers/ticketProductScopes'
import { getTranslate } from '../../localization'
import useStyles from './styles/index.styles'
import { SupportTicketDraft } from './types'

interface SupportTicketCreateFormProps extends FormikProps<SupportTicketDraft> {
  isAdmin: boolean
  contextCapabilities?: SupportContextCapabilities
  contextCapabilitiesLoading?: boolean
  contextCapabilitiesUnavailable?: boolean
  /** An in-flight create reported by Apollo, not by Formik. */
  loading: boolean
  onCancel: () => void
}

/**
 * The create form's fields, in the portal's shape.
 *
 * Every field is an @adotel/components widget rather than a raw MUI one (§6.1
 * — InputWithLabel is Standard at 75 screens, raw TextField is Legacy at 13),
 * laid out on the three-column CSS grid (§6.2) and grouped under SectionTitle
 * (§6.3). The screen previously carried 14 useState hooks and a single
 * `canSubmit` boolean whose only output was a disabled button.
 *
 * Separated from index.tsx because that file composes and fetches; it does not
 * render fields (§8.4).
 */
export default function SupportTicketCreateForm({
  values,
  errors,
  touched,
  isSubmitting,
  isValid,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  isAdmin,
  contextCapabilities,
  contextCapabilitiesLoading = false,
  contextCapabilitiesUnavailable,
  loading,
  onCancel
}: SupportTicketCreateFormProps) {
  const classes = useStyles()
  const busy = isSubmitting || loading
  const enabledContextTypes = contextCapabilities?.enabledRefTypes
  const readiness = supportContextReadiness(
    values,
    contextCapabilities,
    contextCapabilitiesLoading
  )
  const bodyFocused = React.useRef(false)
  const confirmationError =
    !values.confirmAffectedEntities && errors.confirmAffectedEntities
      ? getTranslate(errors.confirmAffectedEntities)
      : undefined

  // Errors show once the field has been touched, so a form does not open
  // covered in red before anyone has typed.
  const fieldError = (field: keyof SupportTicketDraft) =>
    touched[field] && errors[field]
      ? getTranslate(errors[field] as string)
      : undefined

  const text = (
    field: keyof SupportTicketDraft,
    label: string,
    extra: Record<string, unknown> = {}
  ) => (
    <InputWithLabel
      label={getTranslate(label)}
      id={field}
      name={field}
      autoComplete="off"
      inputSize="small"
      value={values[field] as string}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setFieldValue(field, event.currentTarget.value)
      }
      onBlur={handleBlur}
      error={Boolean(fieldError(field)) || undefined}
      helpertext={fieldError(field)}
      disabled={busy}
      {...extra}
    />
  )

  /**
   * One end of the trace window: a date in the reader's calendar, and a time.
   *
   * Both write back into the single `YYYY-MM-DDTHH:mm` draft field, so the
   * validator and the payload builder keep the one string they already parse.
   *
   * The controls redden but carry no message of their own: every trace rule
   * reports through traceId, because a boundary is not wrong on its own, only
   * relative to the other end. The section's one Alert says what is wrong;
   * repeating it under all four boxes would read as four separate faults.
   */
  const traceBoundary = (
    field: 'traceFrom' | 'traceTo',
    label: string,
    timeLabel: string
  ) => {
    const value = values[field]
    // Editing either half marks traceId touched, and traceId is the field the
    // section's message hangs on — every trace rule reports through it,
    // because a window is not wrong on its own, only relative to the other
    // end. Formik's handleBlur keys off the input's `name`, so the time input
    // marked `${field}Time`, a name no schema rule reads, and the date control
    // had no blur handler at all. Between them nothing ever marked traceId
    // touched during entry, and the section stayed silent until a failed
    // submit told the reporter what they had done three fields ago.
    const commit = (next: string) => {
      setFieldValue(field, next)
      setFieldTouched('traceId', true, false)
    }
    return (
      <React.Fragment key={field}>
        <DatePickerInputWithLabel
          label={getTranslate(label)}
          // Without this the label has nothing to point at: the component
          // builds both htmlFor and the control's id from `name`.
          name={field}
          autoComplete="off"
          inputSize="small"
          disabled={busy}
          /* The outline reddens, but the MESSAGE stays in the section's one
             Alert. Every trace rule reports through traceId, so `errors` never
             carries anything under this field's own name — reading it here was
             dead, and repeating the section's single message under all four
             boxes would read as four separate faults. */
          error={Boolean(fieldError('traceId')) || undefined}
          datePickerProps={{
            range: false,
            numberOfVisibleMonths: 1,
            // Without this the picker defaults to future-only, and a trace
            // window is ALWAYS in the past — the reporter is describing
            // something that already happened. The default would have left
            // every day they need greyed out, with the calendar giving no
            // reason why. It permits past days and today, not the future,
            // which is exactly the range a trace can occupy.
            enablePastDays: true,
            dateObject: { gregorianStartDate: traceDatePart(value) },
            onChange: (picked: DateObject) => {
              const day = picked.gregorianStartDate || ''
              commit(joinTraceBoundary(day, traceTimePart(value)))
              // Choosing a start date seeds the end date with the same day,
              // because a window capped at an hour is nearly always same-day
              // and the reporter would otherwise open a second calendar to
              // pick the day they just picked. Only when the end is still
              // empty, and freely overridable — the midnight-crossing case
              // stays expressible.
              if (field === 'traceFrom' && !traceDatePart(values.traceTo)) {
                setFieldValue(
                  'traceTo',
                  joinTraceBoundary(day, traceTimePart(values.traceTo))
                )
              }
            }
          }}
        />
        <InputWithLabel
          label={getTranslate(timeLabel)}
          id={`${field}Time`}
          name={`${field}Time`}
          type="time"
          inputSize="small"
          disabled={busy}
          value={traceTimePart(value)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            commit(
              joinTraceBoundary(traceDatePart(value), event.currentTarget.value)
            )
          }
        />
      </React.Fragment>
    )
  }

  const showEntity = !isAdmin || values.reporterMode !== 'CALLER'

  return (
    <form onSubmit={handleSubmit}>
      {readiness.checking && (
        <Alert severity="info" role="status">
          {getTranslate('Checking available ticket context…')}
        </Alert>
      )}
      {readiness.affectedLimitExceeded && (
        <Alert severity="warning">
          {getTranslate(
            'Remove affected entities to meet the limit of {limit}.',
            { limit: readiness.maxAffectedEntities }
          )}
        </Alert>
      )}
      {readiness.contextLimitExceeded && (
        <Alert severity="warning">
          {getTranslate(
            'Remove related records or the trace to meet the context limit of {limit}.',
            { limit: readiness.maxContextHints }
          )}
        </Alert>
      )}
      <SectionTitle title={getTranslate('Ticket details')} marginBottom />
      <div className={classes.inputsContainer}>
        {text('title', 'Title')}
        {/* A Select rather than free text, because a scope this form cannot
            spell wrong is the only way the reporter cannot silently choose an
            SLA. ticket-service accepts any non-empty string and SLA policies
            match on it with a wildcard fallback, so a typo did not fail — it
            quietly swapped the deadline. See helpers/ticketProductScopes. */}
        <SelectWithLabel
          label={getTranslate('Product area')}
          id="productScope"
          name="productScope"
          size="small"
          value={values.productScope}
          onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
            setFieldValue('productScope', event.target.value)
          }
          onBlur={handleBlur}
          error={Boolean(fieldError('productScope')) || undefined}
          helpertext={fieldError('productScope')}
          disabled={busy}>
          {TICKET_PRODUCT_SCOPES.map((scope) => (
            <MenuItem key={scope.value} value={scope.value}>
              {getTranslate(scope.label)}
            </MenuItem>
          ))}
        </SelectWithLabel>
      </div>

      {(values.relatedEntities.length > 0 ||
        (values.relatedWalletTransactions?.length || 0) > 0 ||
        values.relatedDistribution.length > 0 ||
        values.relatedPromotions.length > 0) && (
        <>
          <SectionTitle title={getTranslate('Related records')} marginBottom />
          <Alert severity="info">
            {getTranslate(
              'Related records provide context and do not grant ticket access.'
            )}
          </Alert>
          {!contextCapabilitiesLoading &&
            ((values.relatedEntities.length > 0 &&
              !enabledContextTypes?.includes('ASSET_ENTITY')) ||
              values.relatedDistribution.some(
                (record) => !enabledContextTypes?.includes(record.refType)
              ) ||
              (values.relatedPromotions.length > 0 &&
                !enabledContextTypes?.includes('PROMOTION')) ||
              ((values.relatedWalletTransactions?.length || 0) > 0 &&
                !enabledContextTypes?.includes('WALLET_TRANSACTION'))) && (
              <Alert severity="warning">
                {getTranslate(
                  'Related records cannot be attached right now. You can still create this ticket.'
                )}
              </Alert>
            )}
          {(values.relatedWalletTransactions || []).map((record, index) => {
            const label =
              (values.relatedWalletTransactions?.length || 0) > 1
                ? getTranslate('Wallet transaction {number}', {
                    number: index + 1
                  })
                : getTranslate('Wallet transaction')
            const remove = () =>
              setFieldValue(
                'relatedWalletTransactions',
                (values.relatedWalletTransactions || []).filter(
                  (item) => item.id !== record.id
                )
              )
            return (
              <Chip
                key={`wallet:${record.id}`}
                label={label}
                aria-label={`${getTranslate('Remove related record')} ${label}`}
                disabled={busy}
                onClick={remove}
                onDelete={remove}
              />
            )
          })}
          {values.relatedEntities.map((entity) => {
            const remove = () =>
              setFieldValue(
                'relatedEntities',
                values.relatedEntities.filter((item) => item !== entity)
              )
            return (
              <Chip
                key={`${entity.type}:${entity.code}`}
                label={entity.label || entity.code}
                aria-label={`${getTranslate('Remove related record')} ${
                  entity.label || entity.code
                }`}
                disabled={busy}
                onClick={remove}
                onDelete={remove}
              />
            )
          })}
          {values.relatedDistribution.map((record) => {
            const remove = () =>
              setFieldValue(
                'relatedDistribution',
                values.relatedDistribution.filter((item) => item !== record)
              )
            return (
              <Chip
                key={JSON.stringify(record)}
                label={record.label}
                aria-label={`${getTranslate('Remove related record')} ${
                  record.label
                }`}
                disabled={busy}
                onClick={remove}
                onDelete={remove}
              />
            )
          })}
          {values.relatedPromotions.map((record) => {
            const remove = () =>
              setFieldValue(
                'relatedPromotions',
                values.relatedPromotions.filter((item) => item !== record)
              )
            return (
              <Chip
                key={`promotion:${record.id}`}
                label={record.label || getTranslate('Promotion')}
                aria-label={`${getTranslate('Remove related record')} ${
                  record.label || getTranslate('Promotion')
                }`}
                disabled={busy}
                onClick={remove}
                onDelete={remove}
              />
            )
          })}
        </>
      )}
      {values.affectedEntities.length > 0 && (
        <>
          <SectionTitle title={getTranslate('Affected')} marginBottom />
          <Alert severity="info">
            {getTranslate(
              'Adding these entities lets their authorized users view this ticket.'
            )}
          </Alert>
          {values.affectedEntities.map((entity) => (
            <Chip
              key={`${entity.type}:${entity.code}`}
              label={entity.label || entity.code}
              disabled={busy}
              onDelete={() => {
                setFieldValue(
                  'affectedEntities',
                  values.affectedEntities.filter((item) => item !== entity)
                )
                setFieldValue('confirmAffectedEntities', false)
              }}
            />
          ))}
          <CheckboxWithLabel
            inputSize="small"
            id="confirmAffectedEntities"
            noHelperText
            error={Boolean(confirmationError)}
            inputProps={{
              'aria-invalid': Boolean(confirmationError),
              'aria-describedby': confirmationError
                ? 'confirm-affected-entities-error'
                : undefined
            }}
            checked={values.confirmAffectedEntities}
            disabled={busy}
            placeholder={getTranslate(
              'I confirm these affected entities may view this ticket.'
            )}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue(
                'confirmAffectedEntities',
                event.currentTarget.checked
              )
            }
          />
          {confirmationError && (
            <FormHelperText error id="confirm-affected-entities-error">
              {confirmationError}
            </FormHelperText>
          )}
        </>
      )}
      <SectionTitle title={getTranslate('Reporter')} marginBottom />
      <div className={classes.inputsContainer}>
        {isAdmin && (
          <SelectWithLabel
            label={getTranslate('Reporter')}
            id="reporterMode"
            name="reporterMode"
            size="small"
            value={values.reporterMode}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
              setFieldValue('reporterMode', event.target.value)
            }>
            <MenuItem value="CALLER">{getTranslate('Myself')}</MenuItem>
            <MenuItem value="CUSTOMER_USER">
              {getTranslate('Customer user')}
            </MenuItem>
            <MenuItem value="EXTERNAL">
              {getTranslate('Phone or email contact')}
            </MenuItem>
          </SelectWithLabel>
        )}
        {showEntity && text('entityType', 'Reporter entity type')}
        {showEntity && text('entityCode', 'Reporter entity code')}
        {isAdmin &&
          values.reporterMode === 'CUSTOMER_USER' &&
          text('customerUserId', 'Customer user ID')}
        {isAdmin && values.reporterMode === 'EXTERNAL' && (
          <>
            {text('externalName', 'Contact name')}
            {text('externalEmail', 'Email', {
              disabled: busy || values.externalPhone !== ''
            })}
            {/* Iranian example: the placeholder was a Swedish number in an
                Iran-market product. */}
            {text('externalPhone', 'Phone (E.164)', {
              placeholder: '+989121234567',
              disabled: busy || values.externalEmail !== ''
            })}
          </>
        )}
      </div>

      {unavailableContextForProduct(
        values.productScope,
        enabledContextTypes
      ) && (
        <Alert severity="info" className={classes.traceAlert}>
          {getTranslate(
            'Context auto-attach is not available for this product yet. Add the relevant identifiers and details in the description.'
          )}
        </Alert>
      )}
      {contextCapabilitiesUnavailable && (
        <Alert severity="info">
          {getTranslate(
            'Could not check automatic context availability. You can still create this ticket.'
          )}
        </Alert>
      )}

      {values.failedOperation && (
        <>
          <SectionTitle
            title={getTranslate('Selected failed operation')}
            marginBottom
          />
          <Chip
            label={values.failedOperation.operationName}
            aria-label={`${getTranslate('Remove selected failure')} ${
              values.failedOperation.operationName
            }`}
            disabled={busy}
            onDelete={() => setFieldValue('failedOperation', undefined)}
            onClick={() => setFieldValue('failedOperation', undefined)}
          />
          <Typography variant="body2">
            {getTranslate('Request correlation')}:{' '}
            <span dir="ltr">{values.failedOperation.correlationId}</span>
          </Typography>
          <Alert severity="info">
            {getTranslate(
              'Request correlation identifies this failed request; it does not automatically attach a trace.'
            )}
          </Alert>
          <Alert severity="warning">
            {getTranslate(
              'Selected failure cannot be attached right now. You can still create this ticket.'
            )}
          </Alert>
        </>
      )}
      <SectionTitle title={getTranslate('Diagnostic context')} marginBottom />
      {/* `placeholder` is this widget's text-beside-the-box: it becomes the
          FormControlLabel's label, which wraps the input and so associates
          natively. A hand-rolled <Typography> next to it would look identical
          and be unassociated — the defect this migration exists to remove. */}
      <CheckboxWithLabel
        inputSize="small"
        id="attachTrace"
        noHelperText
        placeholder={getTranslate('Attach diagnostic trace')}
        checked={values.attachTrace}
        disabled={busy}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setFieldValue('attachTrace', event.currentTarget.checked)
        }
      />

      {values.attachTrace && (
        <>
          <div className={classes.inputsContainer}>
            {text('traceId', 'W3C trace ID')}
          </div>
          {/* A date picker and a time, per boundary, replacing two native
              datetime-local inputs.

              datetime-local renders the browser's Gregorian calendar, in a
              product whose every other date control follows the panel's
              `lang === 'fa' ? 'jalaali' : 'gregorian'` idiom. An operator
              reading Jalali dates on every other screen was asked to pick a
              Gregorian one here.

              Four controls rather than one date and two times, because the
              window is capped at an hour but an hour may cross midnight —
              23:40 to 00:20 is a legitimate window that a single shared date
              cannot express. The alternative is inferring "an end before the
              start means tomorrow", which is a guess the reporter never sees
              and cannot correct. */}
          <Alert severity="info" className={classes.traceAlert}>
            {getTranslate(
              'The window can be up to one hour long, and may cross midnight.'
            )}
          </Alert>
          <div className={classes.inputsContainer}>
            {traceBoundary(
              'traceFrom',
              'Trace window start',
              'Trace window start time'
            )}
            {traceBoundary(
              'traceTo',
              'Trace window end',
              'Trace window end time'
            )}
          </div>
          {/* Chips rather than a comma-separated string, so the separator is
              not something the reporter has to know about and a malformed
              name is rejected as it is entered instead of on submit.

              The draft field stays CSV. buildDiagnosticTraceInput and the
              seven validation rules already split, trim and de-duplicate it,
              and they are the copy the server's payload is built from — so
              adapting here rather than changing that shape keeps one parser
              instead of two. It is lossless because SERVICE_NAME_PATTERN
              admits no commas. */}
          <FreeSoloAutocompleteWithLabel
            name="traceServices"
            label={getTranslate('Trace services')}
            placeholder={getTranslate('ticket-service')}
            size="small"
            fullWidth
            options={[]}
            /* The validator's rule, not a second spelling of it. A name it
               rejects here would also be rejected on submit; this only moves
               the message to the moment of typing. */
            regexPattern={SERVICE_NAME_PATTERN}
            message={getTranslate(
              'Service names use lowercase letters, digits, hyphens and underscores.'
            )}
            value={splitServices(values.traceServices)}
            onChange={(_event: unknown, next: string[]) =>
              setFieldValue('traceServices', next.join(', '))
            }
            disabled={busy}
          />
          {/* The trace rules all report through traceId's error, since they are
              validated as a set — a window is not wrong on its own, only
              relative to the other end. Surfaced as an Alert as well as inline
              so the reason is visible from the submit button. */}
          {touched.traceId && errors.traceId && (
            <Alert severity="warning" className={classes.traceAlert}>
              {getTranslate(errors.traceId as string)}
            </Alert>
          )}
        </>
      )}

      <SectionTitle title={getTranslate('Description')} marginBottom />
      <TextAreaWithLabel
        label={getTranslate('What happened?')}
        id="body"
        name="body"
        rows={5}
        value={values.body}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
          setFieldValue('body', event.currentTarget.value)
        }
        onBlur={handleBlur}
        /* Caret to the end on the first focus.
           A browser selects a textarea's whole value when it is focused by
           TAB, so a reporter arriving from the floating report button — where
           this field opens holding the page they were on — wipes that line
           with their first keystroke, and nothing tells them it happened. The
           two trailing newlines already put the caret where they want to
           type; this makes the caret actually go there.

           First focus only, so returning to the field later does not yank the
           caret away from where the reporter left it. */
        onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) => {
          if (bodyFocused.current) return
          bodyFocused.current = true
          const field = event.currentTarget
          field.setSelectionRange(field.value.length, field.value.length)
        }}
        error={Boolean(fieldError('body')) || undefined}
        helpertext={
          fieldError('body') ||
          getTranslate(
            'This becomes the first comment on the ticket, visible to everyone on it.'
          )
        }
        disabled={busy}
      />

      {/* §4.4: footer, right-aligned, contained primary, labelled for the
          thing being saved rather than "Submit". */}
      <div className={classes.formActions}>
        <Button variant="text" disabled={busy} onClick={onCancel}>
          {getTranslate('Cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            busy ||
            !isValid ||
            readiness.checking ||
            readiness.affectedLimitExceeded ||
            readiness.contextLimitExceeded
          }>
          {busy ? getTranslate('Creating…') : getTranslate('Create ticket')}
        </Button>
      </div>
    </form>
  )
}
